import React, { useContext, useEffect, useState } from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import EventDrawer from './EventDrawer';
import { AppContext } from '@edx/frontend-platform/react';
import useCalendarEvents from '../../hooks/useCalendarEvents';
import { Modal, Icon, Button } from '@blend-ed/blendx-ui';
import { format } from 'date-fns';
import { useQuery as useReactQuery } from '@tanstack/react-query';
import { getLearnerCourseSchedules } from '../../api/Calendar';
import { useIntl} from '@edx/frontend-platform/i18n';
import messages from '../../components/messages';

const Calendar = () => {
    const intl = useIntl();
    const { authenticatedUser } = useContext(AppContext);
    const [showDrawer, setShowDrawer] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const { events } = useCalendarEvents();

    const todaysEvents = events.filter(event => new Date(event.start).toDateString() === new Date().toDateString()).sort((a, b) => new Date(a.start) - new Date(b.start));
    const upcomingEvents = events.filter(event => new Date(event.start) > new Date()).sort((a, b) => new Date(a.start) - new Date(b.start));

    const calendarOptions = {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
        initialView: 'dayGridMonth',
        dayMaxEventRows: true,
        views: {
            dayGridMonth: {
                buttonText: intl.formatMessage(messages.monthViewButton),
            },
            timeGridDay: {
                buttonText: intl.formatMessage(messages.dayViewButton)
            },
        },
        buttonText: {
            today: intl.formatMessage(messages.todayButton),
            listWeek: intl.formatMessage(messages.showEventsButton),
        },
        headerToolbar: {
            left: 'prev,next title',
            right: 'dayGridMonth,timeGridDay today showEvents',
        },
        droppable: false,
        editable: false,
        eventResizableFromStart: true,
        dayMaxEvents: 5,
        navLinks: true,
        events: events,
        fixedWeekCount: false,
        eventClassNames: function ({ event }) {
            return [
                `fc-${event.extendedProps.variant}`
            ]
        },
        customButtons: {
            showEvents: {
                text: intl.formatMessage(messages.showEventsButton),
                click: function () {
                    setShowDrawer(true);
                }
            }
        },
        eventClick: function (info) {
            setSelectedEvent(info.event);
            setShowModal(true);
        },
    };

    const handleCloseDrawer = () => {
        setShowDrawer(false);
        setSelectedEvent(null);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedEvent(null);
    };

    return (
        <main>
            <div className="calendar">
                <FullCalendar {...calendarOptions} />
            </div>
            <EventDrawer
                showDrawer={showDrawer}
                handleCloseDrawer={handleCloseDrawer}
                todaysEvents={todaysEvents}
                upcomingEvents={upcomingEvents}
            />
            <Modal
                show={showModal}
                type="basic"
                onClose={handleCloseModal}
            >
                <Modal.Header
                    title={intl.formatMessage(messages.viewEventTitle)}
                    size="sm"
                    onClose={handleCloseModal}
                />
                <Modal.Body>
                    <div className="calendar-modal-body">
                        {selectedEvent?.extendedProps?.courseName && !selectedEvent?.extendedProps?.link && (
                            <div className="calendar-modal-input-wrapper">
                                <Icon icon="book" type="line" size="md" />
                                <span>{selectedEvent.extendedProps.courseName}</span>
                            </div>
                        )}
                        <div className="calendar-modal-input-wrapper">
                            <Icon icon="pencil" type="fill" size="md" />
                            <span>{selectedEvent?.title}</span>
                        </div>
                        <div className="calendar-modal-input-wrapper">
                            <Icon icon="calendar" type="line" size="md" />
                            <span>{selectedEvent ? format(new Date(selectedEvent.start), "dd-MMM-yyyy hh:mm a") : ''}</span>
                        </div>
                        {selectedEvent?.extendedProps?.description && (
                            <div className="calendar-modal-input-wrapper">
                                <Icon icon="file-text" type="line" size="md" />
                                <span>{selectedEvent.extendedProps.description}</span>
                            </div>
                        )}
                        {selectedEvent?.extendedProps?.link && (
                            <div className="calendar-modal-input-wrapper">
                                <Icon icon="links" type="line" size="md" />
                                <span>
                                    <a href={selectedEvent.extendedProps.link} target="_blank" rel="noopener noreferrer">
                                        {selectedEvent.extendedProps.linkText || intl.formatMessage(messages.viewDetails)}
                                    </a>
                                </span>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCloseModal}>
                        {intl.formatMessage(messages.closeButton)}
                    </Button>
                    {selectedEvent?.extendedProps?.link && (
                        <Button
                            variant="primary"
                            onClick={() => window.open(selectedEvent.extendedProps.link, '_blank')}
                        >
                            {intl.formatMessage(messages.goToLinkButton)}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </main>
    )
}

export default Calendar;